export enum ActionType {
  // CRUD operations
  CREATE = "CREATE",
  CREATE_VERSION = "CREATE_VERSION",
  CREATE_LABEL = "CREATE_LABEL",
  UPDATE_DATASET = "UPDATE_DATASET",
  UPDATE = "UPDATE",
  DELETE = "DELETE",

  // Eval operations
  RUN_BATCH = "RUN_BATCH",

  // Data operations
  DUPLICATE = "DUPLICATE",

  // Folder operations
  MOVE = "MOVE",

  // Naming operations
  RENAME = "RENAME",

  // Label operations
  MOVE_LABEL = "MOVE_LABEL",
  DELETE_LABEL = "DELETE_LABEL",
  DEPLOY_LABEL = "DEPLOY_LABEL",

  // Other
  CUSTOM = "CUSTOM",
}

// Type alias for folder-compatible entity types (for API validation)
export type FolderEntityType =
  | "FOLDER"
  | "PROMPT"
  | "SNIPPET"
  | "WORKFLOW"
  | "DATASET"
  | "REPORT"
  | "AB_TEST"
  | "INPUT_VARIABLE_SET"
  | "SMART_TABLE"
  | "SKILL_COLLECTION"
  | "TOOL";

export enum EntityType {
  FOLDER = "FOLDER",
  PROMPT = "PROMPT",
  WORKFLOW = "WORKFLOW",
  DATASET = "DATASET",
  REPORT = "REPORT",
  AB_TEST = "AB_TEST",
  SNIPPET = "SNIPPET",
  INPUT_VARIABLE_SET = "INPUT_VARIABLE_SET",
  SMART_TABLE = "SMART_TABLE",
  SKILL_COLLECTION = "SKILL_COLLECTION",
  TOOL = "TOOL",
}

const SUPPORTED_ENTITY_TYPES = new Set<string>(Object.values(EntityType));

export const isSupportedEntityType = (type: string): type is EntityType =>
  SUPPORTED_ENTITY_TYPES.has(type);

export const filterSupportedEntities = <T extends { type: string }>(entities: T[]): T[] =>
  entities.filter(entity => isSupportedEntityType(entity.type));

interface VersionedEntityMetadata {
  latest_version_number?: number;
}

interface PromptMetadata extends VersionedEntityMetadata {
  type: "chat" | "completion";
}

type WorkflowMetadata = VersionedEntityMetadata;

interface DatasetMetadata extends VersionedEntityMetadata {
  isDraft?: boolean;
}

type ReportMetadata = VersionedEntityMetadata;
type ABTestMetadata = VersionedEntityMetadata;

interface InputVariableSetMetadata {
  variable_count?: number;
}

interface SmartTableMetadata {
  sheet_count?: number;
}

type SkillCollectionMetadata = Record<string, never>;

interface ToolMetadata {
  prompt_count?: number;
  latest_version_number?: number;
}

type EntityMetadata<T extends EntityType> = T extends EntityType.PROMPT
  ? PromptMetadata
  : T extends EntityType.WORKFLOW
    ? WorkflowMetadata
    : T extends EntityType.DATASET
      ? DatasetMetadata
      : T extends EntityType.REPORT
        ? ReportMetadata
        : T extends EntityType.AB_TEST
          ? ABTestMetadata
          : T extends EntityType.INPUT_VARIABLE_SET
            ? InputVariableSetMetadata
            : T extends EntityType.SMART_TABLE
              ? SmartTableMetadata
              : T extends EntityType.SKILL_COLLECTION
                ? SkillCollectionMetadata
                : T extends EntityType.TOOL
                  ? ToolMetadata
                  : never;

export interface EntityPreviewAsset {
  status: "pending" | "ready" | "failed";
  image_url?: string;
  generated_at?: string;
  entity_version_key?: string;
  content_hash?: string;
  is_regenerating?: boolean;
}

export interface Entity<T extends EntityType = EntityType> {
  id: number | string;
  name: string;
  type: T;
  created_at?: string;
  updated_at?: string;
  folder_id?: number;
  path: string[];
  metadata?: EntityMetadata<T>;
  preview?: EntityPreviewAsset;
  distance?: number | null;
  version_number?: number;
  label?: string;
  _touched?: boolean;
  created_by_email?: string | null;
  /** TOOL only: latest version has execution code. */
  has_execution?: boolean;
  /** TOOL only: version numbers with execution. */
  versions_with_execution?: number[];
  /** TOOL only: label names pointing at versions with execution. */
  labels_with_execution?: string[];
  /** Function name from the tool's latest non-archived version. May differ from `name` (the registry tool's display name). Used by FunctionsModal so prompts show the function name immediately on add. */
  function_name?: string;
}

export const getEntityNumericId = (id: number | string | undefined | null): number | undefined => {
  if (typeof id === "number") {
    return Number.isFinite(id) ? id : undefined;
  }

  if (typeof id === "string") {
    const parsedId = Number(id);
    return Number.isFinite(parsedId) ? parsedId : undefined;
  }

  return undefined;
};

export const getRequiredEntityNumericId = (id: number | string | undefined | null): number => {
  const numericId = getEntityNumericId(id);

  if (numericId === undefined) {
    throw new Error(`Expected numeric entity id but received: ${String(id)}`);
  }

  return numericId;
};

export interface ListFolderEntitiesRequest {
  filter_types?: EntityType[];
  folder_id: number | null;
  search_query?: string;
  semantic_search?: boolean;
  semantic_search_threshold?: number;
  semantic_search_top_k?: number;
  flatten?: boolean;
  include_previews?: boolean;
  user_id?: number | null;
  tags?: string[];
}

export interface MoveFolderEntityRequest {
  entities: Entity[];
  folder_id: number | null;
}

export interface MoveFolderEntityResponse {
  success: boolean;
  message: unknown;
}

export interface ListFolderEntitiesResponse {
  entities: Entity[];
}

export interface DeleteFolderEntitiesRequest {
  entities: Pick<Entity, "id" | "type">[];
  cascade?: boolean;
}

export interface DeleteFolderEntitiesResponse {
  moved_count: number;
}

interface CreateActionPayload {
  name?: string;
  dataset_id?: number;
  dataset_group_id?: number;
  is_blueprint?: boolean;
  folder_id?: number | null;
}

interface CreateVersionActionPayload {
  prompt_template_version_id?: number;
  prompt_template_name?: string;
  prompt_template_version_number?: number;
  commit_message?: string | null;
  workflow_version_number?: number;
  dataset_group_name?: string;
  dataset_id?: number;
  dataset_group_id?: number;
  workflow_name?: string;
}

export interface CreateLabelActionPayload {
  prompt_label_id?: number | null;
  label_name?: string;
  prompt_template_version_id?: number;
  prompt_template_version_number?: number;
  prompt_template_name?: string;
  workflow_version_number?: number;
  workflow_name?: string;
}

interface UpdateActionPayload {
  fields_changed?: string[];
  old_values?: Record<string, unknown>;
  new_values?: Record<string, unknown>;
}

interface DeleteActionPayload {
  cascade?: boolean;
  name?: string;
}

interface RunBatchActionPayload {
  name?: string;
  batch_run_id?: number;
  dataset_id?: number;
  dataset_refreshed?: boolean;
  action?: "batch_run" | "rerun_column";
  report_column_id?: number;
  report_column_name?: string;
  cells_retried?: number;
  rows_retried?: number;
}

interface DuplicateActionPayload {
  name?: string;
  blueprint_id?: number;
  blueprint_name?: string;
  source_workspace_id?: number;
  target_workspace_id?: number;
  is_blueprint?: boolean;
  columns_duplicated?: number;
}

interface MoveActionPayload {
  from_folder_id?: number | null;
  to_folder_id?: number | null;
}

interface RenameActionPayload {
  old_name: string;
  new_name: string;
}

// Base prompt label fields shared across label operations
interface PromptLabelBasePayload {
  prompt_template_id?: number;
  prompt_template_name?: string;
  prompt_label_id?: number;
  label_name?: string;
}

// Base workflow label fields shared across label operations
interface WorkflowLabelBasePayload {
  workflow_name?: string;
  workflow_label_id?: number;
  label_name?: string;
}

// Prompt move label specific fields
interface PromptMoveLabelPayload extends PromptLabelBasePayload {
  old_prompt_template_version_number?: number;
  new_prompt_template_version_number?: number;
}

// Workflow move label specific fields
interface WorkflowMoveLabelPayload extends WorkflowLabelBasePayload {
  old_workflow_version_id?: number;
  old_workflow_version_number?: number;
  new_workflow_version_id?: number;
  new_workflow_version_number?: number;
}

// Skill collection label fields
interface SkillCollectionLabelBasePayload {
  skill_collection_name?: string;
  skill_collection_label_id?: number;
  label_name?: string;
}

// Skill collection move label specific fields
interface SkillCollectionMoveLabelPayload extends SkillCollectionLabelBasePayload {
  old_skill_collection_version_id?: number;
  old_skill_collection_version_number?: number;
  new_skill_collection_version_id?: number;
  new_skill_collection_version_number?: number;
}

// Tool label fields
interface ToolLabelBasePayload {
  tool_name?: string;
  tool_label_id?: number;
  label_name?: string;
}

// Tool move label specific fields
interface ToolMoveLabelPayload extends ToolLabelBasePayload {
  old_tool_version_id?: number;
  old_tool_version_number?: number;
  new_tool_version_id?: number;
  new_tool_version_number?: number;
}

// Combined move label payload (for backward compatibility)
type MoveLabelActionPayload =
  | PromptMoveLabelPayload
  | WorkflowMoveLabelPayload
  | SkillCollectionMoveLabelPayload
  | ToolMoveLabelPayload;

// Prompt delete label specific fields
interface PromptDeleteLabelPayload extends PromptLabelBasePayload {
  prompt_template_version_number?: number;
}

// Workflow delete label specific fields
interface WorkflowDeleteLabelPayload extends WorkflowLabelBasePayload {
  workflow_version_id?: number;
  workflow_version_number?: number;
}

// Skill collection delete label specific fields
interface SkillCollectionDeleteLabelPayload extends SkillCollectionLabelBasePayload {
  skill_collection_version_id?: number;
  skill_collection_version_number?: number;
}

// Tool delete label specific fields
interface ToolDeleteLabelPayload extends ToolLabelBasePayload {
  tool_version_id?: number;
  tool_version_number?: number;
}

// Combined delete label payload (exported for backward compatibility)
export type DeleteLabelActionPayload =
  | PromptDeleteLabelPayload
  | WorkflowDeleteLabelPayload
  | SkillCollectionDeleteLabelPayload
  | ToolDeleteLabelPayload;

// Deployment metadata added to deployed label operations
interface DeploymentMetadata {
  original_changelog_id: number;
  original_action_type: "MOVE_LABEL" | "DELETE_LABEL";
  deployed_by: number;
}

// Deploy label payload variants
type DeployPromptMovePayload = PromptMoveLabelPayload & DeploymentMetadata;
type DeployPromptDeletePayload = PromptDeleteLabelPayload & DeploymentMetadata;
type DeployWorkflowMovePayload = WorkflowMoveLabelPayload & DeploymentMetadata;
type DeployWorkflowDeletePayload = WorkflowDeleteLabelPayload & DeploymentMetadata;
type DeploySkillCollectionMovePayload = SkillCollectionMoveLabelPayload & DeploymentMetadata;
type DeploySkillCollectionDeletePayload = SkillCollectionDeleteLabelPayload & DeploymentMetadata;
type DeployToolMovePayload = ToolMoveLabelPayload & DeploymentMetadata;
type DeployToolDeletePayload = ToolDeleteLabelPayload & DeploymentMetadata;

// Union type for all deploy label payloads
export type DeployLabelActionPayload =
  | DeployPromptMovePayload
  | DeployPromptDeletePayload
  | DeployWorkflowMovePayload
  | DeployWorkflowDeletePayload
  | DeploySkillCollectionMovePayload
  | DeploySkillCollectionDeletePayload
  | DeployToolMovePayload
  | DeployToolDeletePayload;

export interface UpdateDatasetActionPayload {
  action: "update_dataset";
  old_dataset_id: number | null;
  dataset_group_id: number;
  dataset_id: number;
  dataset_version: number;
}

type CustomActionPayload = Record<string, unknown>;

type ActionPayload<T extends ActionType> = T extends ActionType.CREATE
  ? CreateActionPayload
  : T extends ActionType.CREATE_VERSION
    ? CreateVersionActionPayload
    : T extends ActionType.CREATE_LABEL
      ? CreateLabelActionPayload
      : T extends ActionType.UPDATE
        ? UpdateActionPayload
        : T extends ActionType.DELETE
          ? DeleteActionPayload
          : T extends ActionType.RUN_BATCH
            ? RunBatchActionPayload
            : T extends ActionType.DUPLICATE
              ? DuplicateActionPayload
              : T extends ActionType.MOVE
                ? MoveActionPayload
                : T extends ActionType.RENAME
                  ? RenameActionPayload
                  : T extends ActionType.MOVE_LABEL
                    ? MoveLabelActionPayload
                    : T extends ActionType.DELETE_LABEL
                      ? DeleteLabelActionPayload
                      : T extends ActionType.DEPLOY_LABEL
                        ? DeployLabelActionPayload
                        : T extends ActionType.UPDATE_DATASET
                          ? UpdateDatasetActionPayload
                          : T extends ActionType.CUSTOM
                            ? CustomActionPayload
                            : Record<string, unknown>;

export interface Change<T extends ActionType = ActionType> {
  id: number;
  workspace_id: number;
  user_id: number | null;
  action_type: T;
  action_payload: ActionPayload<T> | null;
  entity_type: EntityType;
  entity_id: string | null;
  datetime: string;
  approved: boolean;
  approved_by: number | null;
}

export interface GetChangelogRequest {
  entity_id: number;
  entity_type: EntityType;
  page?: number;
  per_page?: number;
  include_unapproved?: boolean;
  start_date?: string;
  end_date?: string;
  user_id?: number;
  pending?: boolean;
}

export interface ChangelogUser {
  email: string;
  name: string | null;
}

export interface ChangelogEntry<T extends ActionType = ActionType> {
  id: number;
  workspace_id: number;
  user_id: number | null;
  action_type: T;
  action_payload: ActionPayload<T> | null;
  entity_type: EntityType;
  entity_id: number | null;
  datetime: string;
  approved: boolean;
  merged: boolean;
  approved_by: number | null;
  user: ChangelogUser | null;
  approved_by_user: ChangelogUser | null;
}

export interface CreateDatasetVersionActionPayload {
  dataset_id: number;
  dataset_group_id: number;
  dataset_group_name: string;
  dataset_version_number: number; // -1 for draft (group creation), ≥1 for published versions
  rows_count: number;
  source:
    | "dataset_group_creation" // When a new dataset group is created (version -1)
    | "manual_save" // Dashboard save button (draft → version 1+)
    | "filter_params" // API: create from request log filters
    | "file_upload" // API: create from CSV/JSON file
    | "filter_params_legacy"; // Legacy API endpoint
  folder_id?: number; // Only present during dataset_group_creation
  file_name?: string; // Only present when source is "file_upload"
}

export interface GetChangelogResponse {
  items: ChangelogEntry[];
  has_prev: boolean;
  has_next: boolean;
  prev_num: number | null;
  next_num: number | null;
  page: number;
  pages: number;
  per_page: number;
  total: number;
}

export interface GetGlobalChangelogRequest {
  page?: number;
  per_page?: number;
  start_date?: string;
  end_date?: string;
  user_id?: number;
  pending?: boolean;
}

export interface GetGlobalChangelogResponse {
  items: ChangelogEntry[];
  has_prev: boolean;
  has_next: boolean;
  prev_num: number | null;
  next_num: number | null;
  page: number;
  pages: number;
  per_page: number;
  total: number;
}

export interface ApproveChangelogRequest {
  approve: boolean;
}

export interface ApproveChangelogResponse {
  success: boolean;
  message: string;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface DeployChangelogRequest {}

export interface DeployChangelogResponse {
  success: boolean;
  message: string;
  changelog: ChangelogEntry;
}
